import type { Order, OrderKind, OrderStatus } from '../api/types';

/*
 * Como cada etapa do pedido é chamada, para o cliente e para a equipe.
 *
 * Mora num lugar só porque três telas falam do mesmo pedido — a do cliente, a
 * lista "Meus pedidos" e o painel — e o nome de uma etapa não pode mudar de
 * uma tela para a outra.
 *
 * As etapas são os status que o banco já tem. O fluxo da encomenda:
 *
 *   enviado      1. Pedido enviado para o atendimento, aguardando compra
 *   processando  2. Compra realizada, aguarde a proposta de troca
 *   concluido    3. Compra finalizada
 *
 * Upgrade segue o mesmo caminho. Na venda de inventário é a loja que compra do
 * cliente, então a etapa 2 é a proposta de troca que ele precisa aceitar.
 */

export const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** Os status que formam o caminho do pedido, na ordem. */
export const ETAPAS: OrderStatus[] = ['enviado', 'processando', 'concluido'];

export interface TextoEtapa {
  titulo: string;
  detalhe: string;
}

const TEXTO_CLIENTE: Record<OrderKind, Record<'enviado' | 'processando' | 'concluido', TextoEtapa>> = {
  encomenda: {
    enviado: {
      titulo: 'Pedido enviado para o atendimento',
      detalhe: 'Aguardando compra. O atendimento vai comprar as suas skins.',
    },
    processando: { titulo: 'Compra realizada', detalhe: 'Aguarde a proposta de troca na Steam.' },
    concluido: { titulo: 'Compra finalizada', detalhe: 'As skins foram entregues no seu inventário.' },
  },
  upgrade: {
    enviado: {
      titulo: 'Pedido enviado para o atendimento',
      detalhe: 'Aguardando compra das skins que você escolheu.',
    },
    processando: {
      titulo: 'Compra realizada',
      detalhe: 'Aguarde a proposta de troca na Steam: ela leva as suas skins e traz as novas.',
    },
    concluido: { titulo: 'Upgrade finalizado', detalhe: 'A troca foi concluída.' },
  },
  venda: {
    enviado: {
      titulo: 'Pedido enviado para o atendimento',
      detalhe: 'Aguardando a análise das suas skins.',
    },
    processando: { titulo: 'Proposta de troca enviada', detalhe: 'Aceite a troca na Steam para entregar as skins.' },
    concluido: { titulo: 'Venda finalizada', detalhe: 'O valor combinado foi pago.' },
  },
};

/** A etapa como o cliente lê, na tela do pedido. */
export function textoParaCliente(kind: OrderKind, status: OrderStatus): TextoEtapa {
  if (status === 'aberto') {
    return { titulo: 'Pedido criado', detalhe: 'Falta enviar ao atendimento para a compra começar.' };
  }
  if (status === 'cancelado') return { titulo: 'Pedido cancelado', detalhe: '' };
  return TEXTO_CLIENTE[kind][status];
}

/** Nome curto da etapa, para selo e coluna do painel. */
export function rotuloCurto(kind: OrderKind, status: OrderStatus): string {
  switch (status) {
    case 'aberto':
      return 'Não enviado';
    case 'enviado':
      return kind === 'venda' ? 'Aguardando análise' : 'Aguardando compra';
    case 'processando':
      return kind === 'venda' ? 'Proposta enviada' : 'Compra realizada';
    case 'concluido':
      return 'Finalizado';
    case 'cancelado':
      return 'Cancelado';
  }
}

/** Posição do status no caminho: 0 antes de enviar, 1 a 3 nas etapas. */
export function posicaoDaEtapa(status: OrderStatus): number {
  const i = ETAPAS.indexOf(status);
  return i === -1 ? 0 : i + 1;
}

/** O próximo passo que a equipe dá, com o texto do botão. */
export function proximoPasso(kind: OrderKind, status: OrderStatus): { status: OrderStatus; acao: string } | null {
  if (status === 'aberto' || status === 'enviado') {
    return { status: 'processando', acao: kind === 'venda' ? 'Marcar proposta enviada' : 'Marcar compra realizada' };
  }
  if (status === 'processando') {
    return { status: 'concluido', acao: kind === 'venda' ? 'Finalizar venda' : 'Finalizar pedido' };
  }
  return null;
}

export const NOME_DO_TIPO: Record<OrderKind, string> = {
  encomenda: 'Encomenda',
  upgrade: 'Upgrade',
  venda: 'Venda de inventário',
};

/** O valor que o pedido representa, conforme o tipo. */
export function valorDoPedido(o: Pick<Order, 'kind' | 'totalBrl' | 'creditBrl' | 'toPayBrl'>): number {
  return o.kind === 'upgrade' ? o.toPayBrl : o.kind === 'venda' ? o.creditBrl : o.totalBrl;
}

/**
 * Recado pronto para avisar o cliente pelo WhatsApp dele.
 *
 * O site não manda mensagem sozinho: a equipe clica e revisa antes de enviar.
 * O texto usa o mesmo nome de etapa que o cliente vê na tela do pedido.
 */
export function recadoParaCliente(o: Pick<Order, 'code' | 'kind' | 'status' | 'personaName' | 'cancelReason'>): string {
  const nome = o.personaName ? `Olá, ${o.personaName}!` : 'Olá!';
  if (o.status === 'cancelado') {
    return `${nome} O pedido ${o.code} foi cancelado${o.cancelReason ? `: ${o.cancelReason}` : '.'}`;
  }
  const t = textoParaCliente(o.kind, o.status);
  return `${nome} Atualização do pedido ${o.code}: *${t.titulo}*. ${t.detalhe}`.trim();
}

/** "há 3 h", "há 2 dias": quanto tempo o pedido está parado na etapa. */
export function tempoDesde(iso: string, agora = Date.now()): string {
  const min = Math.max(0, Math.round((agora - new Date(iso).getTime()) / 60000));
  if (min < 60) return `há ${min} min`;
  const h = Math.round(min / 60);
  if (h < 48) return `há ${h} h`;
  return `há ${Math.round(h / 24)} dias`;
}

/**
 * Link do WhatsApp para o número que o cliente informou no cadastro.
 *
 * O cadastro aceita o número do jeito que a pessoa digita, e na prática ele
 * chega sem o código do país: "(11) 99999-0000", 11 dígitos. O wa.me exige o
 * número internacional, e sem o 55 o link abre uma conversa que não existe.
 * Número de 10 ou 11 dígitos é tratado como brasileiro; com mais, já veio com
 * o código do país. Um 0 na frente (hábito de interurbano) é descartado.
 */
export function linkWhatsapp(numero: string | null | undefined, texto?: string): string | null {
  let d = (numero ?? '').replace(/\D/g, '').replace(/^0+/, '');
  if (d.length === 10 || d.length === 11) d = `55${d}`;
  if (d.length < 12 || d.length > 15) return null;
  return `https://wa.me/${d}${texto ? `?text=${encodeURIComponent(texto)}` : ''}`;
}
